import {
  HHInfoGrid,
  HHLink,
  HHStack,
  HHTypography,
} from '@hinge-health/react-component-library';
import dayjs from 'dayjs';
import { routes } from '../../../constants/strings/routes';
import { BillSubmissionsType, useGetContractsV2Query } from '../../../types';
import { formatCurrency } from '../../../utils/currency-helpers';
import { displayDateTime } from '../../../utils/date-helper';
import { BillAmountMismatchIcon } from '../bill-grid-components/bill-amount-mismatch-icon';
import { displayBillFilterStatus } from '../utils';

interface BillDrawerDetailsProps {
  billDetails: BillSubmissionsType;
  isBillAmountValid: boolean;
  currency: string | null;
  displayLocal: boolean;
}

const BillDrawerDetails = ({
  billDetails,
  isBillAmountValid,
  currency,
  displayLocal,
}: BillDrawerDetailsProps): JSX.Element => {
  const { data: contractsData } = useGetContractsV2Query({
    variables: {
      contractIds: [billDetails.contractId ?? 0],
      page: 1,
      size: 1,
      // Include voided contracts so a bill on a voided contract still resolves its
      // client/insurer for display instead of showing "Loading..." forever.
      includeVoided: true,
    },
    skip: !billDetails.contractId,
  });

  // Resolved exclusively from the contract
  const clientsInsurerId =
    contractsData?.getContractsV2.items[0]?.clientsInsurerId;

  return (
    <HHStack>
      <HHInfoGrid
        data={[
          {
            content: String(billDetails.id),
            heading: 'Id',
            copyable: true,
          },
          {
            content: billDetails.source,
            heading: 'Source',
            copyable: true,
          },
          {
            content: String(billDetails.sourceId),
            heading: 'Source Id',
            copyable: true,
          },
          {
            content:
              clientsInsurerId != null ? (
                <HHLink
                  href={`${routes.billing.home}/${routes.billing.clientInsurerPath}/${clientsInsurerId}`}
                  target="_blank"
                  variant="body1"
                  underline="none"
                >
                  {clientsInsurerId}
                </HHLink>
              ) : (
                '--'
              ),
            heading: 'Client Insurer ID',
            copyable: false,
          },
          {
            content: String(billDetails.contractId ?? '--'),
            heading: 'Contract Id',
            copyable: true,
          },
          {
            content: String(billDetails.pathwayId ?? '--'),
            heading: 'Pathway Id',
            copyable: true,
          },
          {
            content: dayjs(billDetails.fromDate).format('L'),
            heading: 'DOS',
            copyable: false,
          },
          {
            content: billDetails.claimIdentifier,
            heading: 'Claim ID',
            copyable: true,
          },
          {
            content: billDetails.paymentType,
            heading: 'Payment Type',
            copyable: true,
          },
          {
            content: (
              <HHStack direction="row" alignItems="center" spacing={0.5}>
                <HHTypography
                  hhVariant="body"
                  color={isBillAmountValid ? 'inherit' : 'error'}
                >
                  {typeof billDetails.billAmount === 'number'
                    ? formatCurrency(billDetails.billAmount, currency, false)
                    : '--'}
                </HHTypography>
                {!isBillAmountValid && <BillAmountMismatchIcon />}
              </HHStack>
            ),
            heading: 'Amount',
            copyable: false,
          },
          {
            content: displayBillFilterStatus(
              billDetails.internalHolds ?? [],
              billDetails.submissions ?? [],
              billDetails.status,
            ),
            heading: 'Status',
            copyable: false,
          },
          {
            content: displayDateTime(billDetails.createdAt, {
              displayLocal,
            }),
            heading: 'Created At',
            copyable: false,
          },
        ]}
        divided={false}
        justifyBetween={true}
      />
    </HHStack>
  );
};

export default BillDrawerDetails;
